import type { Lead } from '../../types/crm'
import { cleanCell, normalizeName } from '../../data/transform'
import { sortLeadsAlphabetically } from '../leads/leadDisplay'

export type MissingInformationFieldKey =
  | 'company'
  | 'owner'
  | 'website'
  | 'facebook'
  | 'linkedin'
  | 'email'
  | 'phone'
  | 'city'
  | 'state'
  | 'notes'

export type MissingInformationField = {
  key: MissingInformationFieldKey
  label: string
  inputMode?: 'email' | 'tel' | 'url'
  multiline?: boolean
}

export type MissingInformationUpdates = Partial<Record<MissingInformationFieldKey, string>>

const MISSING_VALUES = new Set([
  '',
  '-',
  'na',
  'n/a',
  'none',
  'not available',
  'not provided',
  'not identified',
  'unknown',
  'does not have a website',
])

const ORGANIZATION_FIELDS: Array<MissingInformationField & { read: (lead: Lead) => string }> = [
  { key: 'company', label: 'Company name', read: (lead) => lead.organization?.name ?? '' },
  { key: 'website', label: 'Website', inputMode: 'url', read: (lead) => lead.organization?.website ?? '' },
  { key: 'facebook', label: 'Facebook', inputMode: 'url', read: (lead) => lead.organization?.facebook ?? '' },
  { key: 'linkedin', label: 'LinkedIn', inputMode: 'url', read: (lead) => lead.organization?.linkedin ?? '' },
  { key: 'email', label: 'Email', inputMode: 'email', read: (lead) => lead.organization?.email ?? '' },
  { key: 'phone', label: 'Phone', inputMode: 'tel', read: (lead) => lead.organization?.phone ?? '' },
  { key: 'city', label: 'City', read: (lead) => lead.organization?.city ?? '' },
  { key: 'state', label: 'State', read: (lead) => lead.organization?.state ?? '' },
]

export function isMissingInformationValue(value: unknown): boolean {
  const normalized = cleanCell(value).toLocaleLowerCase().replace(/[.!]+$/, '')
  return MISSING_VALUES.has(normalized)
}

export function getMissingInformationFields(lead: Lead): MissingInformationField[] {
  const fields: MissingInformationField[] = []

  if (lead.organization) {
    for (const field of ORGANIZATION_FIELDS) {
      if (isMissingInformationValue(field.read(lead))) {
        const { read: _read, ...definition } = field
        fields.push(definition)
      }
    }
  }

  if (lead.opportunity && isMissingInformationValue(lead.opportunity.name)) {
    fields.splice(lead.organization && fields[0]?.key === 'company' ? 1 : 0, 0, { key: 'owner', label: 'Owner name' })
  }

  const notesValue = lead.opportunity?.notes ?? lead.organization?.notes ?? ''
  if ((lead.opportunity || lead.organization) && isMissingInformationValue(notesValue)) {
    fields.push({ key: 'notes', label: 'Notes', multiline: true })
  }

  return fields
}

export function searchLeadsForReview(leads: Lead[], query: string): Lead[] {
  const normalizedQuery = normalizeName(query)
  if (!normalizedQuery) return []

  return sortLeadsAlphabetically(leads.filter((lead) => {
    const company = normalizeName(lead.organization?.name ?? lead.company)
    const owner = normalizeName(lead.opportunity?.name ?? lead.owner)
    return company.includes(normalizedQuery) || owner.includes(normalizedQuery)
  }))
}

export function getEnteredUpdates(
  fields: MissingInformationField[],
  values: MissingInformationUpdates,
): MissingInformationUpdates {
  const updates: MissingInformationUpdates = {}
  for (const field of fields) {
    const value = cleanCell(values[field.key])
    if (value) updates[field.key] = value
  }
  return updates
}

export function validateMissingInformationUpdates(
  fields: MissingInformationField[],
  updates: MissingInformationUpdates,
): Partial<Record<MissingInformationFieldKey, string>> {
  const errors: Partial<Record<MissingInformationFieldKey, string>> = {}
  const editable = new Set(fields.map((field) => field.key))

  for (const [key, value] of Object.entries(updates) as Array<[MissingInformationFieldKey, string]>) {
    if (!editable.has(key)) {
      errors[key] = 'This existing field is not editable.'
      continue
    }
    if (isMissingInformationValue(value)) {
      errors[key] = 'Enter a real value instead of a placeholder.'
      continue
    }
    if (key === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[key] = 'Enter a valid email address.'
    }
    if (key === 'phone') {
      const digitCount = value.replace(/\D/g, '').length
      if (!/^[+()\d.\-\s]+$/.test(value) || digitCount < 7 || digitCount > 15) {
        errors[key] = 'Enter a valid phone number.'
      }
    }
    if (key === 'website' || key === 'facebook' || key === 'linkedin') {
      try {
        const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`)
        if (!['http:', 'https:'].includes(url.protocol) || !url.hostname.includes('.')) throw new Error('Invalid URL')
      } catch {
        errors[key] = 'Enter a valid web address.'
      }
    }
  }

  return errors
}
